import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../client";
import type { Community, CreatorSummary, Page, Post } from "../types";
import { createOptimisticHandlers } from "./optimistic";
import { qk } from "./qk";
import type { CommunityListParams } from "./qk";

export const communitiesQuery = (params: CommunityListParams = {}) =>
  queryOptions({
    queryKey: qk.communities.list(params),
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.q) search.set("q", params.q);
      if (params.category) search.set("category", params.category);
      const query = search.toString();
      return apiGet<Community[]>(`/api/communities${query ? `?${query}` : ""}`);
    },
    staleTime: 120_000, // plan §5: catalog list
  });

export const communityQuery = (slug: string) =>
  queryOptions({
    queryKey: qk.communities.detail(slug),
    queryFn: () => apiGet<Community>(`/api/communities/${slug}`),
    staleTime: 300_000, // plan §5: entity detail
  });

export const communityPostsInfiniteQuery = (slug: string) =>
  infiniteQueryOptions({
    queryKey: qk.communities.posts(slug),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      apiGet<Page<Post>>(`/api/communities/${slug}/posts?limit=20${pageParam ? `&cursor=${pageParam}` : ""}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: Page<Post>) => lastPage.nextCursor,
    staleTime: 60_000,
  });

export const communityMembersInfiniteQuery = (slug: string) =>
  infiniteQueryOptions({
    queryKey: qk.communities.members(slug),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      apiGet<Page<CreatorSummary>>(`/api/communities/${slug}/members?limit=30${pageParam ? `&cursor=${pageParam}` : ""}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: Page<CreatorSummary>) => lastPage.nextCursor,
    staleTime: 300_000,
  });

export interface JoinCommunityVariables {
  slug: string;
  joined: boolean;
}

export function useJoinCommunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, joined }: JoinCommunityVariables) =>
      joined
        ? apiPost<{ joined: boolean; members: number }>(`/api/communities/${slug}/membership`)
        : apiDelete<{ joined: boolean; members: number }>(`/api/communities/${slug}/membership`),
    ...createOptimisticHandlers<JoinCommunityVariables>({
      queryClient,
      action: "join-community",
      affectedKeys: ({ slug }) => [qk.communities.detail(slug), qk.communities.list({})],
      apply: ({ slug, joined }) => [
        {
          queryKey: qk.communities.detail(slug),
          updater: (previous: Community | undefined) =>
            previous ? { ...previous, joined, members: previous.members + (joined ? 1 : -1) } : previous,
        },
        {
          queryKey: qk.communities.list({}),
          updater: (previous: Community[] | undefined) =>
            previous?.map((community) =>
              community.slug === slug ? { ...community, joined, members: community.members + (joined ? 1 : -1) } : community,
            ),
        },
      ],
    }),
  });
}
